import type { ZonesTypes } from '@/types/aggregation/common';
import type {
    AppMarketingBannerSlice,
    AppMarketingSliceItem,
    AppMarketingSlicePrimary,
    AppMarketingTilesSlice
} from '@/features/app-marketing/types/app-marketing-slice';
import type {
    FaqSectionSlice,
    FaqSectionSliceItem,
    FaqSectionSlicePrimary
} from '@/types/zones/slices/faq-section-slice';
import type { Zone, ZoneData } from '@/types/zones/zone';
import { ZONE_SLICE_TYPES } from '@/app/constants/zone';
import { hasAppMarketingRenderableContent } from '@/features/app-marketing/logic/app-marketing-app-links';

export type AppMarketingFaqContent = {
    primary: FaqSectionSlicePrimary;
    items: FaqSectionSliceItem[];
};

export type ParsedAppMarketingZoneContent = {
    primary: AppMarketingSlicePrimary;
    items: AppMarketingSliceItem[];
    faq: AppMarketingFaqContent | null;
};

export type AppMarketingZoneBodySlice = AppMarketingBannerSlice | AppMarketingTilesSlice | FaqSectionSlice;

export type AppMarketingZone = Zone & {
    data: Omit<Zone['data'], 'body'> & {
        body: AppMarketingZoneBodySlice[];
    };
};

export const getZoneBody = (zone: Zone | ZonesTypes): ZoneData['body'] | undefined => {
    const body = (zone?.data as Partial<ZoneData> | undefined)?.body;
    return Array.isArray(body) ? body : undefined;
};

export const getZoneFirstSliceType = (zone: Zone | ZonesTypes) => getZoneBody(zone)?.[0]?.slice_type;

const isAppMarketingLandingSlice = (sliceType: string | undefined): boolean =>
    sliceType === ZONE_SLICE_TYPES.APP_MARKETING_BANNER ||
    sliceType === ZONE_SLICE_TYPES.APP_MARKETING_TILES ||
    sliceType === ZONE_SLICE_TYPES.FAQ_SECTION;

const zoneHasAppMarketingSlices = (body: ZoneData['body']): boolean =>
    body.some(
        (slice) =>
            slice.slice_type === ZONE_SLICE_TYPES.APP_MARKETING_BANNER ||
            slice.slice_type === ZONE_SLICE_TYPES.APP_MARKETING_TILES
    );

const zoneContributesToAppMarketingLanding = (body: ZoneData['body']): boolean =>
    body.some((slice) => isAppMarketingLandingSlice(slice.slice_type));

/** Prismic may split banner, tiles, and FAQ across multiple zones; merge slices for one landing view. */
export const mergeAppMarketingZoneBodies = (zones?: Array<Zone | ZonesTypes>): ZoneData['body'] => {
    if (!zones?.length) {
        return [];
    }

    return zones.flatMap((zone) => {
        const body = getZoneBody(zone);
        if (!body?.length || !zoneContributesToAppMarketingLanding(body)) {
            return [];
        }

        return body.filter((slice) => isAppMarketingLandingSlice(slice.slice_type));
    });
};

const parseAppMarketingFaqFromBody = (body: ZoneData['body'] | undefined): AppMarketingFaqContent | null => {
    const slice = (Array.isArray(body) ? body : []).find((entry) => entry.slice_type === ZONE_SLICE_TYPES.FAQ_SECTION);
    if (!slice) {
        return null;
    }

    const primary = (slice.primary ?? {}) as FaqSectionSlicePrimary;
    const items = (slice.items ?? []) as FaqSectionSliceItem[];
    if (!primary.title?.length && !primary.description?.length && items.length === 0) {
        return null;
    }

    return { primary, items };
};

export const parseAppMarketingZoneBody = (body: ZoneData['body'] | undefined): ParsedAppMarketingZoneContent => {
    const slices = Array.isArray(body) ? body : [];
    let primary: AppMarketingSlicePrimary = {};
    const items: AppMarketingSliceItem[] = [];
    let hasAppMarketingContent = false;

    for (const slice of slices) {
        if (slice.slice_type === ZONE_SLICE_TYPES.APP_MARKETING_BANNER) {
            hasAppMarketingContent = true;
            primary = { ...primary, ...((slice.primary ?? {}) as AppMarketingSlicePrimary) };
        }
        if (slice.slice_type === ZONE_SLICE_TYPES.APP_MARKETING_TILES && Array.isArray(slice.items)) {
            if (slice.items.length > 0) {
                hasAppMarketingContent = true;
            }
            items.push(...(slice.items as AppMarketingSliceItem[]));
        }
    }

    return {
        primary,
        items,
        faq: hasAppMarketingContent ? parseAppMarketingFaqFromBody(body) : null
    };
};

export const isAppMarketingZone = (zone: Zone | ZonesTypes): zone is AppMarketingZone => {
    const body = getZoneBody(zone);
    if (!body?.length || !zoneHasAppMarketingSlices(body)) {
        return false;
    }

    const { primary, items } = parseAppMarketingZoneBody(body);
    return hasAppMarketingRenderableContent(primary, items);
};

export const hasAppMarketingZone = (zones?: Array<Zone | ZonesTypes>): boolean =>
    Boolean(zones?.some((zone) => isAppMarketingZone(zone)));

export const showAppMarketing = (isWebBrowser: boolean, zones?: Array<Zone | ZonesTypes>): boolean =>
    isWebBrowser && hasAppMarketingZone(zones);
